import React, { useState, useRef, ReactNode, useEffect } from 'react';
import { Action } from 'redux';
import { useWtrDispatch, useWtrSelector } from 'redux/hooks';
import { useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { Button } from '@johnlewispartnership/wtr-ingredients/ingredients/Button';
import { Modal } from '@johnlewispartnership/wtr-ingredients/ingredients/Modal';
import { Snackbar } from '@johnlewispartnership/wtr-ingredients/ingredients/Snackbar';
import { Alert } from '@johnlewispartnership/wtr-ingredients/ingredients/Alert';
import {
  CircleAdd,
  RemoveOutline,
  Information,
  Close,
  TrolleyInactive,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { Tooltip } from '@johnlewispartnership/wtr-ingredients/ingredients/Tooltip';
import { Helmet } from 'react-helmet-async';
import { redirectToLogin } from 'utils/login';
import { FifoBuffer, fifoBufferLookup, fifoBufferPush } from 'utils/fifo-buffer';
import { dataLayer } from 'analytics/data-layer';
import { local as storage } from 'utils/storage';
import { Filter, filteredRecipes } from 'services/recipes-landing-service';
import { Recipe, RecipeSummaryItem } from 'api/definitions/recipes/index.d';
import { getCookies } from 'redux/modules/cookies/selectors';
import { getAccessToken, isUserLoggedIn } from 'redux/modules/sessions/selectors';
import { DEFAULT_SORT_BY } from 'redux/modules/recipes/constants';
import { getRecipeById } from 'redux/modules/recipes/selectors/';
import { fetchRecipeById } from 'redux/modules/recipes/actions/get-recipe';
import { getProductsByRecipeId } from 'redux/modules/recipes/actions';
import { getRecipeCardsGrid } from 'redux/modules/cms-hybrid-recipes-page/selectors';
import { type RecipeStateItem, defaultRecipe } from 'redux/modules/recipes/reducers';
import { mergeFilters } from 'redux/modules/cms-hybrid-recipes-page/reducers';
import { MEAL_PLANNER_UPDATE } from 'redux/modules/meal-planner/actions/types';
import { getMealPlanner } from 'redux/modules/meal-planner/selectors';
import { RecipeCard } from 'components/Recipes/Content/RecipeCard';
import { emptyFilter } from 'components/Recipes/MealPlanner/utils/filter';
import root from 'window-or-global';
import { MEALPLANNER_PATH } from '../utils/path';
import MealPlannerAddToTrolleyModal from '../AddToTrolleyModal';
import { RecipeSelection } from '../RecipeSelection';
import { RecipeDetail } from '../../Content/RecipeDetail';
import { getFiltersFromSearch, FilterTitle, FilterTitles } from '../Filters';
import type { SearchFilter } from '../Filters';
import HowItWorksModal from '../HowItWorksModal';
import WaitroseLogo from '../../../WaitroseLogo';
import ServesModal from '../ServesModal';
import { getURLParamValue } from '../../../../redux/modules/recipes/utils/get-url-param-value';
import styles from './index.scss';
import { useClientSideMediaQuery } from '../../../../hooks/use-client-side-media-query';
import { BREAKPOINTS } from '../../../../constants/grid';

type DayOfWeek = 'Monday' | 'Tuesday';
const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

type SwapRecipe = {
  /**
    All available filters, even ones with showing 0
  */
  allFilters: Filter[];
  /**
    Their current selection.. We update to get updated Filter numbers
  */
  selectedFilters: SearchFilter[];
  loading: boolean;
  recipes: RecipeSummaryItem[];
  totalMatches: number;
};

const pageSize = 5;

const usePaginatedRecipes = () => {
  const { filters: globalFilters } = useWtrSelector(getMealPlanner);
  const { pathname, search } = useLocation();
  const { dietaryFilters = [] } = useWtrSelector(getRecipeCardsGrid);
  const jwt = useWtrSelector(getAccessToken);

  const defaultRecipeData = {
    allFilters: globalFilters,
    selectedFilters: getFiltersFromSearch(new URLSearchParams(search)),
    loading: false,
    recipes: [],
    totalMatches: 0,
  };

  const [selectedRecipeData, setSelectedRecipeData] = useState<SwapRecipe>(defaultRecipeData);

  const filterResultsBuffer = useRef<FifoBuffer<SwapRecipe>>([]);
  const appliedFiltersRef = useRef<SearchFilter[]>([]);

  useEffect(() => {
    if (globalFilters?.length) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedRecipeData(prevState => ({
        ...prevState,
        allFilters: mergeFilters(prevState.allFilters, globalFilters),
      }));
    }
  }, [globalFilters]);

  const dietaryValues = (
    globalFilters.find(filter => filter.title === 'Dietary') || emptyFilter
  ).values.map(v => v.name);

  const minCount = (filter: Filter) => {
    if (filter.title === 'Dietary') {
      return {
        ...filter,
        values: filter.values.filter(value => {
          return dietaryValues.includes(value.name);
        }),
      };
    }

    return filter;
  };

  const filterDietary = (value?: Filter) =>
    pathname.endsWith('meal-plans/builder') || value?.title !== 'Dietary';

  const fetchRecipes = (
    recipe: Recipe['id'] | DayOfWeek,
    exclude: Recipe['id'][],
    searchFilters?: SearchFilter[],
    clear: boolean = false,
  ) => {
    const swapRecipe = days.includes(recipe) ? 'empty' : recipe;
    const queryStringFilters = getFiltersFromSearch(new URLSearchParams(search));

    const selectedFilters =
      (searchFilters?.length && searchFilters) ||
      (appliedFiltersRef.current?.length && appliedFiltersRef.current) ||
      (queryStringFilters.length && queryStringFilters) ||
      [];

    const cacheKey = JSON.stringify({
      dietaryFilters,
      exclude,
      swapRecipe,
      selectedFilters,
    });

    const cachedData = fifoBufferLookup(filterResultsBuffer.current, cacheKey);

    if (cachedData && clear) {
      setSelectedRecipeData(cachedData);
      return;
    }

    setSelectedRecipeData(prevState => ({
      ...prevState,
      loading: true,
    }));

    const combinedFilters = [
      { title: 'Course', values: ['Main meal'] }, // Add "Main Meal" filter dynamically
      ...selectedFilters,
      ...(dietaryFilters.length && !selectedFilters.find(f => f.title === 'Dietary')
        ? [{ title: 'Dietary', values: dietaryFilters }]
        : []),
    ];

    filteredRecipes(
      jwt,
      [],
      combinedFilters,
      DEFAULT_SORT_BY,
      undefined,
      (!clear && selectedRecipeData.recipes.length) || 0,
      pageSize,
      exclude,
      true,
      true,
    ).then(response => {
      const allFilters = mergeFilters(globalFilters, selectedRecipeData?.allFilters);

      const newData: SwapRecipe = {
        recipes: (clear ? [] : selectedRecipeData.recipes).concat(response.recipes),
        allFilters: mergeFilters(
          allFilters,
          response.filters
            .filter(({ title }) => FilterTitles.includes(title as FilterTitle))
            .map(minCount),
        ).filter(filterDietary),
        selectedFilters,
        totalMatches: response.totalMatches,
        loading: false,
      };

      filterResultsBuffer.current = fifoBufferPush(filterResultsBuffer.current, cacheKey, newData);

      setSelectedRecipeData(newData);
    });
  };

  const applyFilters = () => {
    appliedFiltersRef.current = structuredClone(selectedRecipeData?.selectedFilters || []);
  };

  const resetFilters = (
    recipe: Recipe['id'] | DayOfWeek,
    excludedRecipes: (RecipeSummaryItem | null)[],
  ) => {
    fetchRecipes(
      recipe,
      excludedRecipes.map(excludedRecipe => (excludedRecipe ? excludedRecipe.id : '')),
      appliedFiltersRef.current,
      true,
    );
  };

  const clear = () => {
    appliedFiltersRef.current = [];

    setSelectedRecipeData(defaultRecipeData);
  };

  return [selectedRecipeData, fetchRecipes, applyFilters, resetFilters, clear] as const;
};

const EmptyCard = ({
  day,
  children,
  onClick,
}: {
  day: string;
  children: ReactNode;
  onClick: () => void;
}) => (
  <li className={classnames(styles['empty-card'], styles.day, styles[day.toLowerCase()])}>
    <button type="button" onClick={onClick}>
      <span>Add recipe</span>
    </button>
    {children}
  </li>
);

const ConfirmRemoveCard = ({
  day,
  onYes,
  onNo,
}: {
  day: string;
  onYes: () => void;
  onNo: () => void;
}) => (
  <li className={classnames(styles['confirm-remove'], styles.day, styles[day.toLowerCase()])}>
    <div>
      Are you sure you want to remove this recipe from your meal plan?
      <button type="button" onClick={onYes}>
        Yes
      </button>
      <button type="button" onClick={onNo}>
        No
      </button>
    </div>
  </li>
);

const RecipeSnackbar = ({
  show,
  msg,
  onClose,
}: {
  show: boolean;
  msg: string;
  onClose: () => void;
}) => {
  return show ? (
    <Snackbar
      open
      placement="topRight"
      snapTo="parent"
      type="success"
      onClose={onClose}
      duration={4000}
      style={{ width: 'calc(100% - 32px)', minWidth: 150, marginTop: 36, zIndex: 1 }}
    >
      {msg}
    </Snackbar>
  ) : null;
};

interface MealPlannerGridProps {
  title?: string;
  subtitle?: string;
}

const { history } = root;

const MEAL_PLANNER_PERSISTED_RECIPES_KEY = 'meal_planner_recipes';

interface OrderButtonProps {
  onClick: () => void;
  disabled: boolean;
  className?: string;
  loading?: boolean;
  dataTestId?: string;
}

export const OrderButton = ({
  onClick,
  disabled,
  className,
  loading,
  dataTestId,
}: OrderButtonProps) => {
  if (loading) {
    return null;
  }
  return (
    <Button
      startIcon={<TrolleyInactive />}
      theme="finalising"
      onClick={onClick}
      disabled={disabled}
      className={className}
      data-testid={dataTestId}
    >
      Order Online
    </Button>
  );
};

export const MealPlannerGrid = ({ title = 'Your meal plan', subtitle }: MealPlannerGridProps) => {
  const dispatch = useWtrDispatch();
  const location = useLocation();
  const { pathname } = location;

  const { recipes, loading, fewMatches } = useWtrSelector(getMealPlanner);
  const [trolleyModal, setTrolleyModal] = useState<boolean>(false);
  const [focusedRecipe, setFocusedRecipe] = useState<Recipe['id']>('');
  const [activeRecipe, setActiveRecipe] = useState<Recipe['id']>('');
  const [showRecipeDetail, setShowRecipeDetail] = useState<Recipe['id']>('');
  const [swapRecipe, fetchRecipes, applyFilters, resetFilters, clearSwapRecipes] =
    usePaginatedRecipes();
  const recipeDetail = useWtrSelector(state => getRecipeById(showRecipeDetail)(state));
  const [[snackMsg, snackDay], setSnackMsg] = useState<[string, number]>(['', 0]);
  const [howItWorksModal, setHowItWorksModal] = useState<boolean>(false);
  const [confirmRemove, setConfirmRemove] = useState<Recipe['id']>('');
  const [tooltipStep, setTooltipStep] = useState<number>(0);
  const [tutorialComplete, setTutorialComplete] = useState<boolean>(
    storage.get('mealPlanTutorialComplete') === 'true',
  );
  const { showInitialModal } = useWtrSelector(getCookies);

  const servingSize = history?.state?.servingSize || getURLParamValue('Serving Size');
  const [showServesModal, setShowServesModal] = useState<boolean>(false);

  const moreRecipesAvailable =
    !!swapRecipe.recipes.length && swapRecipe.recipes.length < swapRecipe.totalMatches;

  const fetch = (searchFilters?: SearchFilter[], clear?: boolean) =>
    fetchRecipes(
      activeRecipe,
      recipes.map(recipe => (recipe ? recipe.id : '')),
      searchFilters,
      clear,
    );

  const ratings = useRef<HTMLDetailsElement | null>(null);

  const summaryAsRecipe = (summary: RecipeSummaryItem | undefined): Partial<RecipeStateItem> => ({
    ...defaultRecipe,
    ...summary,
  });

  const detailRecipe =
    (recipeDetail?.id && recipeDetail) ||
    summaryAsRecipe(
      recipes.find(recipe => recipe?.id === showRecipeDetail) ||
        swapRecipe?.recipes.find(recipe => recipe.id === showRecipeDetail),
    );

  const onSwap = (id: Recipe['id'] | undefined) => {
    const wasEmpty = days.includes(activeRecipe);
    const index = wasEmpty
      ? days.indexOf(activeRecipe)
      : recipes.findIndex(recipe => recipe?.id === activeRecipe);

    setShowRecipeDetail('');
    setActiveRecipe('');
    clearSwapRecipes(); // recipe exclude-list has changed

    dispatch({
      type: MEAL_PLANNER_UPDATE,
      recipes: recipes.map((recipe, day) =>
        [recipe?.id, days[day]].includes(activeRecipe)
          ? swapRecipe.recipes.find(r => r.id === id) || null
          : recipe,
      ),
    });

    dispatch(fetchRecipeById(id)).then(() => {
      dispatch(getProductsByRecipeId(id));
    });

    setSnackMsg([wasEmpty ? 'Recipe added to plan' : 'Recipe swapped successfully', index]);

    dataLayer.push({
      event: 'recipe_added',
      recipe_name: id,
      index,
      method: 'swapped',
    });
  };

  const handleNextStep = () => {
    if (tooltipStep === 1) {
      setTutorialComplete(true);
      storage.set('mealPlanTutorialComplete', 'true');
    } else {
      setTooltipStep(prev => prev + 1);
    }
  };

  const handleCloseTooltip = () => {
    setTutorialComplete(true);
    storage.set('mealPlanTutorialComplete', 'true');
  };

  useEffect(() => {
    if (servingSize || !pathname.includes(MEALPLANNER_PATH) || loading || showInitialModal) {
      return undefined;
    }

    const timer = setTimeout(() => setShowServesModal(true), 2000);

    return () => clearTimeout(timer);
  }, [servingSize, pathname, loading, showInitialModal]);

  const navigate = useNavigate();
  const [cookieModalWasShown, setCookieModalWasShown] = useState<boolean>(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCookieModalWasShown(showInitialModal);
    if (cookieModalWasShown && !showInitialModal) {
      navigate(0);
    }
  }, [showInitialModal, cookieModalWasShown, navigate]);

  const noRecipes = !recipes.some(Boolean);

  const isLoggedIn = useWtrSelector(isUserLoggedIn);

  const onOrderClick = () => {
    if (!isLoggedIn) {
      sessionStorage.setItem(MEAL_PLANNER_PERSISTED_RECIPES_KEY, JSON.stringify(recipes));
      redirectToLogin();
    } else {
      setTrolleyModal(true);
    }
  };

  const isMobile = useClientSideMediaQuery({ maxWidth: BREAKPOINTS.sm });

  useEffect(() => {
    if (isLoggedIn) {
      const persistedRecipesData = sessionStorage.getItem(MEAL_PLANNER_PERSISTED_RECIPES_KEY);
      if (persistedRecipesData) {
        try {
          const persistedRecipes = JSON.parse(persistedRecipesData);

          if (persistedRecipes.some(Boolean)) {
            dispatch({
              type: MEAL_PLANNER_UPDATE,
              recipes: persistedRecipes,
            });

            persistedRecipes.forEach((recipe: { id: string } | null) => {
              if (recipe) {
                dispatch(fetchRecipeById(recipe.id)).then(() => {
                  dispatch(getProductsByRecipeId(recipe.id));
                });
              }
            });
          }

          sessionStorage.removeItem(MEAL_PLANNER_PERSISTED_RECIPES_KEY);
        } catch {
          sessionStorage.removeItem(MEAL_PLANNER_PERSISTED_RECIPES_KEY);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const orderOnlineTopButtonRef = useRef<HTMLDivElement>(null);
  const orderOnlineBottomButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMobile) return;
    const topButton = orderOnlineTopButtonRef.current;
    const bottomButton = orderOnlineBottomButtonRef.current;
    if (!topButton || !bottomButton) return;

    const topButtonInitialPosition = topButton.getBoundingClientRect().top + window.scrollY;

    const handleScroll = () => {
      if (!topButton || !bottomButton) return;

      const scrollPosition = window.scrollY;
      const bottomButtonRect = bottomButton.getBoundingClientRect();

      if (scrollPosition > topButtonInitialPosition) {
        topButton.classList.add(styles.sticky);

        if (bottomButtonRect.top < window.innerHeight && bottomButtonRect.top > 0) {
          topButton.classList.add(styles.hidden);
        } else {
          topButton.classList.remove(styles.hidden);
        }
      } else {
        topButton.classList.remove(styles.sticky);
        topButton.classList.remove(styles.hidden);
      }
    };

    window.addEventListener('scroll', handleScroll);
    // eslint-disable-next-line consistent-return
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobile]);

  return (
    <>
      <Helmet bodyAttributes={{ class: 'meal-planner-builder-page' }} />
      <div className={classnames('no-print', 'container-fluid', styles.fluid)}>
        <Typography
          component="h1"
          styleAs="screenHeadingSmall"
          textStyle="heading-2xl"
          className={styles.h1}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            component="p"
            styleAs="paragraph"
            textStyle="body-m"
            className={styles.subtitle}
          >
            {subtitle}
          </Typography>
        )}
        <div className={styles['how-it-works']}>
          <Button
            startIcon={<Information size={isMobile ? 'medium' : 'small'} />}
            className={styles['how-it-works-button']}
            onClick={() => setHowItWorksModal(true)}
            width="fit"
            theme="secondary"
            aria-label="How it works Modal"
          >
            {!isMobile && 'How it works'}
          </Button>
        </div>
        <HowItWorksModal
          isOpen={howItWorksModal}
          closeModal={() => setHowItWorksModal(false)}
          isMobile={isMobile}
        />
        <ServesModal
          isOpen={showServesModal && !showInitialModal}
          closeModal={() => setShowServesModal(false)}
        />
        {fewMatches && (
          <Alert
            type="info"
            title="Few or no matches found"
            message="Try changing your serving size and dietary preferences by adding a recipe and editing your preferences"
            style={{ maxWidth: 638, margin: 'auto' }}
          />
        )}

        <div
          ref={orderOnlineTopButtonRef}
          className={styles.orderOnlineTopButtonWrapper}
          data-testid="meal-planner-order-button-top-wrapper"
        >
          <OrderButton
            onClick={onOrderClick}
            disabled={noRecipes}
            className={styles.orderOnlineTopButton}
            dataTestId="meal-planner-order-button-top"
          />
        </div>

        <ul className={styles.container}>
          {loading ||
            recipes.map((recipe, i) => {
              if (confirmRemove === recipe?.id) {
                return (
                  <ConfirmRemoveCard
                    day={days[i]}
                    key={days[i]}
                    onYes={() => {
                      dispatch({
                        type: MEAL_PLANNER_UPDATE,
                        recipes: recipes.map((r, day) => (day === i ? null : r)),
                      });

                      setConfirmRemove('');
                      setSnackMsg(['Recipe removed', i]);

                      dataLayer.push({
                        event: 'remove_recipe',
                        recipe_name: recipe.id,
                        index: i,
                      });
                    }}
                    onNo={() => setConfirmRemove('')}
                  />
                );
              }
              if (recipe) {
                return (
                  <RecipeCard
                    key={recipe.id}
                    className={classnames(styles.day, styles[days[i].toLowerCase()])}
                    recipe={recipe}
                    aria-label={days[i]}
                    headingLevel="h2"
                    analytics={{}}
                    onClick={e => {
                      setShowRecipeDetail(recipe.id);
                      setFocusedRecipe(recipe.id);
                      dispatch(fetchRecipeById(recipe.id) as unknown as Action);
                      e.preventDefault();
                    }}
                  >
                    <Tooltip
                      className={classnames(styles.tutorialTooltip, styles.tutorialTooltipTopStart)}
                      placement="top-start"
                      distanceOffset={15}
                      skiddingOffset={8}
                      content={
                        <div className={styles.tutorialTooltipContentContainer}>
                          <div className={styles.tutorialTooltipContentRow}>
                            <Typography styleAs="paragraph" textStyle="body-m" noMargins>
                              Use the swap icon to change a recipe.
                            </Typography>
                            <Close
                              size="small"
                              onClick={handleCloseTooltip}
                              className={styles.tutorialTooltipCloseButton}
                            />
                          </div>

                          <div className={styles.tutorialTooltipContentRow}>
                            <Typography
                              styleAs="paragraph"
                              textStyle="body-m"
                              noMargins
                              className={styles.tutorialTooltipStep}
                            >
                              1/2
                            </Typography>
                            <Button
                              theme="primaryWhite"
                              onClick={handleNextStep}
                              className={styles.tutorialTooltipButton}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      }
                      isOpen={!tutorialComplete && tooltipStep === 0 && i === 0}
                    >
                      <button
                        type="button"
                        className={classnames(styles.swap, styles['card-button'])}
                        title="Swap recipe"
                        onClick={() => {
                          setActiveRecipe(recipe.id);
                          setFocusedRecipe(recipe.id);
                          setSnackMsg(['', 0]);

                          dataLayer.push({
                            event: 'click_swap_recipe',
                            recipe_name: recipe.id,
                            index: i,
                          });
                        }}
                      />
                    </Tooltip>

                    <Tooltip
                      className={classnames(styles.tutorialTooltip, styles.tutorialTooltipTopEnd)}
                      placement="top-end"
                      distanceOffset={15}
                      skiddingOffset={-8}
                      content={
                        <div className={styles.tutorialTooltipContentContainer}>
                          <div className={styles.tutorialTooltipContentRow}>
                            <Typography styleAs="paragraph" textStyle="body-m" noMargins>
                              Remove any recipe by clicking the (X) icon
                            </Typography>
                            <Close
                              size="small"
                              onClick={handleCloseTooltip}
                              className={styles.tutorialTooltipCloseButton}
                            />
                          </div>

                          <div className={styles.tutorialTooltipContentRow}>
                            <Typography styleAs="paragraph" textStyle="body-m" noMargins>
                              2/2
                            </Typography>
                            <Button
                              theme="primaryWhite"
                              onClick={handleNextStep}
                              className={styles.tutorialTooltipButton}
                            >
                              Done
                            </Button>
                          </div>
                        </div>
                      }
                      isOpen={!tutorialComplete && tooltipStep === 1 && i === 0}
                    >
                      <button
                        type="button"
                        className={classnames(styles.remove, styles['card-button'])}
                        title="Remove recipe"
                        onClick={() => setConfirmRemove(recipe.id)}
                      />
                    </Tooltip>
                    <RecipeSnackbar
                      show={!!(snackMsg && i === snackDay)}
                      msg={snackMsg}
                      onClose={() => setSnackMsg(['', i])}
                    />
                  </RecipeCard>
                );
              }

              return (
                <EmptyCard
                  day={days[i]}
                  key={days[i]}
                  onClick={() => {
                    setFocusedRecipe('');
                    setActiveRecipe(days[i]);
                    setSnackMsg(['', 0]);
                    dataLayer.push({
                      event: 'click_add_recipe',
                      index: i,
                    });
                  }}
                >
                  <RecipeSnackbar
                    show={!!(snackMsg && i === snackDay)}
                    msg={snackMsg}
                    onClose={() => setSnackMsg(['', i])}
                  />
                </EmptyCard>
              );
            })}
        </ul>

        <div
          ref={orderOnlineBottomButtonRef}
          className={styles.orderOnlineBottomButtonWrapper}
          data-testid="meal-planner-order-button-bottom-wrapper"
        >
          <OrderButton
            onClick={onOrderClick}
            disabled={noRecipes}
            className={styles.orderOnlineBottomButton}
            loading={loading}
            dataTestId="meal-planner-order-button-bottom"
          />
        </div>
      </div>
      {trolleyModal && (
        <MealPlannerAddToTrolleyModal
          recipes={recipes.filter(Boolean)}
          isOpen={trolleyModal}
          closeModal={() => setTrolleyModal(false)}
        />
      )}
      <Modal
        className={classnames(styles.modal, styles['swap-modal'])}
        contentClassName={styles['swap-modal-content']}
        bodyOpenClassName={styles['modal-body']}
        isOpen={!!activeRecipe.length}
        handleModalClose={() => {
          clearSwapRecipes();
          setActiveRecipe('');
        }}
        onAfterOpen={() => {
          if (!swapRecipe?.recipes?.length) {
            fetch();
          }
        }}
      >
        <RecipeSelection
          recipes={swapRecipe.recipes}
          filters={swapRecipe.allFilters}
          loading={swapRecipe.loading}
          moreRecipesAvailable={moreRecipesAvailable}
          onFilterChange={searchFilters => {
            fetch(searchFilters, true);
          }}
          onSelect={id => onSwap(id)}
          onView={recipeId => {
            setShowRecipeDetail(recipeId);
            dispatch(fetchRecipeById(recipeId) as unknown as Action);
          }}
          loadMore={searchFiltersState => fetch(searchFiltersState)}
          applyFilters={applyFilters}
          resetFilters={() => resetFilters(activeRecipe, recipes)}
          selectedFilters={swapRecipe.selectedFilters}
        />
      </Modal>
      <Modal
        titleText="Recipe preview"
        isOpen={!!showRecipeDetail}
        handleModalClose={() => setShowRecipeDetail('')}
        className={styles['recipe-modal']}
        contentClassName={styles['recipe-modal-content']}
      >
        <WaitroseLogo small className={styles['waitrose-logo']} />
        <RecipeDetail
          recipe={detailRecipe as Recipe}
          loading={false}
          headingLevel={2}
          showPrint
          rating={detailRecipe.rating}
          ratings={ratings}
        />
        <div className={classnames('no-print', styles['sticky-button'])}>
          {detailRecipe.id === focusedRecipe ? (
            <Button
              theme="secondary"
              width="full"
              className={styles['add-recipe']}
              onClick={() => {
                setShowRecipeDetail('');
                dispatch({
                  type: MEAL_PLANNER_UPDATE,
                  recipes: recipes.map(recipe => (recipe?.id === detailRecipe.id ? null : recipe)),
                });
                setSnackMsg(['Recipe removed', recipes.findIndex(r => r?.id === detailRecipe.id)]);
              }}
              startIcon={<RemoveOutline />}
            >
              Remove recipe
            </Button>
          ) : (
            <Button
              theme="primary"
              width="full"
              className={styles['add-recipe']}
              onClick={() => onSwap(detailRecipe.id)}
              startIcon={<CircleAdd />}
            >
              Add recipe
            </Button>
          )}
        </div>
      </Modal>
    </>
  );
};

export default MealPlannerGrid;
